import { QUERY_OUTPUT } from "types/outputFormat";

import { AuthService } from "services/auth";
import { WorkspaceEngine } from "services/engines/engine.types";
import { normalizeResponse } from "services/query/helpers";

import { FIREBOLT_UI_USER_ENGINE_QUERY_LABEL } from "pages/DevelopWorkspace/services/constants";
import { buildEngineUrl } from "pages/DevelopWorkspace/services/helpers/buildEngineUrl";
import {
  FetchOptions,
  RunningQuery,
  RunningQueryStatus,
} from "pages/DevelopWorkspace/services/types";
import { QueryStatementStatus } from "pages/DevelopWorkspace/workspace.types";

export const mapQueryServerStatus = (
  serverStatus: RunningQueryStatus
): QueryStatementStatus | undefined => {
  switch (serverStatus) {
    case RunningQueryStatus.RUNNING:
      return QueryStatementStatus.running;
    case RunningQueryStatus.SUSPENDED:
      return QueryStatementStatus.suspended;
    default:
      return undefined;
  }
};

const getRunningQuery = async (
  engine: WorkspaceEngine,
  queryId: string,
  fetchOptions: FetchOptions,
  authService: AuthService
) => {
  const token = await authService.getToken();
  if (!token) {
    throw new Error("Failed to fetch query progress: token is not available");
  }

  const { headers, abortController } = fetchOptions;

  const engineURL = new URL(`https://${engine.url}`);
  const url = buildEngineUrl(engineURL, {
    query_label: FIREBOLT_UI_USER_ENGINE_QUERY_LABEL,
    auto_start_stop_control: "ignore",
  });

  const isLite = Boolean(process.env.REACT_APP_LITE);

  const response = await fetch(url, {
    signal: abortController.signal,
    method: "POST",
    headers: {
      ...headers,
      Authorization: `Bearer ${token}`,
      "Firebolt-Machine-Query": "1",
    },
    body: `select * from information_schema.engine_running_queries where query_id = '${queryId}'`,
  });

  const json = await response.json();

  const res = isLite
    ? normalizeResponse({
        response: json,
        querySettings: {
          output_format: QUERY_OUTPUT.JSON_COMPACT,
        },
        responseStatusCode: response.status,
      }).data[0]
    : json.data[0];

  if (!res) {
    throw new Error("Query not found");
  }

  const runningQuery: RunningQuery = {
    status: res.status,
    queryLabel: res.query_label,
    queryId: res.query_id,
    durationUsec: res.e2e_duration_us,
    scannedRows: res.scanned_rows,
    scannedBytes: res.scanned_bytes,
    insertedRows: res.inserted_rows,
  };

  return runningQuery;
};

export default getRunningQuery;
